import React, { FC, ReactElement } from 'react';
import {NavLink, Outlet, useMatch, useNavigate, useLocation} from 'react-router-dom';
import {
    Avatar,
    BottomNavigation,
    BottomNavigationAction,
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// Application-specific imports
import { LIBRARY, ROOT, VIDEO, videoPath } from 'routes';
import HomeIcon from '@mui/icons-material/Home';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {useTranslation} from 'react-i18next';
// Icon for queue actions
import ShuffleIcon from '@mui/icons-material/Shuffle';
import TitleToolbar from './TitleToolbar';
import FooterLinks from './FooterLinks';
import {usePlayback} from 'contexts/WatchContext';
import {useActor} from 'contexts/ActorContext';
import PersistentYtPlayer from './PersistentYtPlayer';
import { NoSnippet } from 'utils/NoSnippet';

/**
 * App layout with persistent search bar and outlet for page content.
 */
interface LayoutProps {
  /**
   * If provided, render as embedded children (e.g. TitleSubject) instead of outlet.
   */
  children?: React.ReactNode;
}
const Layout: FC<LayoutProps> = ({ children }): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  // Detect if current route is the TitleSubject page
  const videoMatch = useMatch(VIDEO);
  const isVideoPage = Boolean(videoMatch);
  // Queue and drawer state from context
  const { facadeQueue, shadowQueue, currentVideo, drawerOpen, openDrawer, closeDrawer, play, shuffleQueue } = usePlayback();
  // Refs for video-page placeholders
  const desktopPlaceholderRef = React.useRef<HTMLDivElement>(null);
  const mobilePlaceholderRef = React.useRef<HTMLDivElement>(null);
  const miniPlaceholderRef = React.useRef<HTMLDivElement>(null);
  // Ref for main scrollable content container (mobile)
  const mainScrollRef = React.useRef<HTMLDivElement>(null);
  // Theme and media query must be available before scroll effect
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Scroll to top on any navigation
  React.useEffect(() => {
    // If navigating with a target section (e.g., scrollTo state), skip default scroll-to-top
    const state = (location.state as any) || {};
    if (state.scrollTo) {
      return;
    }
    try {
      if (isMobile) {
        mainScrollRef.current?.scrollTo(0, 0);
      } else {
        window.scrollTo(0, 0);
      }
    } catch {
      // ignore errors in unsupported environments
    }
  }, [location.pathname, isMobile, location.state]);
  const hasQueue = facadeQueue.length > 0;
  const {t} = useTranslation(['titleWelcome', 'titleLibrary', 'titleToolbar']);
  const {actor} = useActor();
  const isOnWelcomePage = location.pathname === ROOT;
  // Drawer widths: mini collapsed and full expanded
  // theme.spacing(10) => '80px', theme.spacing(32) => '256px'
  const fullDrawerWidth = theme.spacing(32);
  // mini collapsed drawer width: fit thumbnail buttons with minimal margins (80px)
  const miniDrawerWidth = theme.spacing(10);
  // Fixed row height for queue items (avatar + 16px vertical padding)
  const rowHeight = `calc(48px + ${theme.spacing(2)})`;
  // Mobile detection is done above for scroll effect
  // Mobile-specific swipe handlers removed: no page-level swipe navigation
  // Drawer content shared between permanent and temporary variants
  const drawerInner = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header: always present to maintain list offset; close button only when expanded */}
      <Toolbar
        // Use regular toolbar height to match AppBar height
        variant="regular"
        disableGutters
        sx={{ justifyContent: 'space-between', px: 1 }}
      >
        <Typography
          variant="h6"
          noWrap
          sx={{ lineHeight: 1.2 }}
        >
          {t('queue')} ({facadeQueue.length})
        </Typography>
        {drawerOpen && (
          <Box sx={(theme) => ({ display: 'flex', gap: theme.spacing(1) })}>
            <IconButton onClick={shuffleQueue} aria-label="Shuffle Queue">
              <ShuffleIcon />
            </IconButton>
            <IconButton onClick={closeDrawer} aria-label="Close Queue">
              <ChevronLeftIcon />
            </IconButton>
          </Box>
        )}
      </Toolbar>
      <Divider />
      {/* Video queue list: reserve scrollbar gutter and optionally hide scrollbar to avoid layout shift */}
      <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
        px: 1,
        // Reserve space for scrollbar to avoid shifting content
        scrollbarGutter: 'stable',
        // Hide default scrollbars
        '&::-webkit-scrollbar': { width: 0, background: 'transparent' },
        scrollbarWidth: 'none',
      }}>
        {drawerOpen ? (
          <List disablePadding>
            {facadeQueue.map((item) => {
              const isActive = currentVideo?.musicVideo === item.musicVideo;
              return (
              <ListItem disablePadding key={item.musicVideo} sx={{ py: 0.5 }}>
            <ListItemButton
                  alignItems="center"
                  sx={{
                    justifyContent: 'flex-start',
                    px: 1,
                    height: rowHeight,
                ...(isActive && {
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.main' },
                }),
                  }}
                  onClick={() => {
                    // Start playback and navigate, preserving facade and shadow queues
                    play(item, facadeQueue);
                    navigate(videoPath(item.musicVideo), {
                      state: { facadeQueue, shadowQueue },
                    });
                    closeDrawer();
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      variant="square"
                      src={item.ssizeThumb}
                      alt={item.trackTitle}
                      sx={{ width: 48, height: 48 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.trackTitle}
                    secondary={item.ownerTitle}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                    sx={{ ml: 1 }}
                  />
                </ListItemButton>
              </ListItem>
              );
            })}
          </List>
        ) : (
          <List disablePadding>
            {facadeQueue.map((item) => {
              const isActive = currentVideo?.musicVideo === item.musicVideo;
              return (
                <ListItem disablePadding key={item.musicVideo} sx={{ py: 0.5 }}>
                  <ListItemButton
                    alignItems="center"
                    sx={{
                      // Center square highlight in mini drawer
                      width: rowHeight,
                      height: rowHeight,
                      mx: 'auto',
                      justifyContent: 'flex-start',
                      px: 1,
                      ...(isActive && {
                        bgcolor: 'primary.main',
                        '&:hover': { bgcolor: 'primary.main' },
                      }),
                    }}
                  onClick={() => {
                    // Start playback and navigate, preserving facade and shadow queues
                    play(item, facadeQueue);
                    navigate(videoPath(item.musicVideo), {
                      state: { facadeQueue, shadowQueue },
                    });
                  }}
                  >
                    <Avatar
                      variant="square"
                      src={item.ssizeThumb}
                      alt={item.trackTitle}
                      sx={{ width: 48, height: 48 }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
    </Box>
  </Box>
  );
  // Navigate to a video context: always push a new history entry
  const onInputSelected = (musicVideo: string): void => {
    navigate(videoPath(musicVideo));
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <NoSnippet>
      {/* Sidebar drawer: permanent on desktop, temporary overlay on mobile */}
      {hasQueue && (isMobile ? (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={closeDrawer}
          ModalProps={{ keepMounted: true, disableScrollLock: true }}
          sx={{
            // Ensure mobile drawer overlays the AppBar
            zIndex: theme.zIndex.drawer + 2,
            '& .MuiDrawer-paper': { width: fullDrawerWidth },
          }}
        >
          {drawerInner}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: 0,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            '& .MuiDrawer-paper': {
              position: 'fixed',
              top: 0,
              left: 0,
              height: '100vh',
              width: drawerOpen ? fullDrawerWidth : miniDrawerWidth,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              zIndex: theme.zIndex.appBar,
            },
          }}
          open={drawerOpen}
        >
          {drawerInner}
        </Drawer>
      ))}
      </NoSnippet>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Shift content right on desktop only when queue present
          ml: !isMobile && hasQueue
            ? (drawerOpen ? fullDrawerWidth : miniDrawerWidth)
            : 0,
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
        }}
      >
      {/* App Bar and spacer: hide on mobile video pages to maximize space */}
      {!(isVideoPage && isMobile) && (
        <NoSnippet>
          <TitleToolbar
            onInputSelected={onInputSelected}
            onOpenDrawer={() => (drawerOpen ? closeDrawer() : openDrawer())}
          />
          {/* Spacer to offset fixed AppBar (search toolbar) */}
          <Toolbar />
        </NoSnippet>
      )}
      {/* Page content and bottom nav for mobile in one scrollable container */}
      {isMobile ? (
        <>
          {/* Page content wrapper with bottom padding */}
          <Box sx={({ spacing }) => ({ pb: `calc(${spacing(2)} + 180px)` })}>
            {children ?? (
              <Outlet context={{
                desktopPlayerPlaceholderRef: desktopPlaceholderRef,
                mobilePlayerPlaceholderRef: mobilePlaceholderRef,
              }} />
            )}
          </Box>
          <NoSnippet>
            {isOnWelcomePage && <FooterLinks />}
            {/* Sticky bottom navigation */}
            {(actor?.actorEmail || !isOnWelcomePage) && (
              <Box
                component="nav"
                sx={({ palette, zIndex }) => ({
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  zIndex: zIndex.appBar,
                  bgcolor: alpha(palette.background.paper, 0.98),
                  borderTop: `1px solid ${alpha(palette.divider, 0.3)}`,
                  boxShadow: '0px -4px 8px rgba(0, 0, 0, 0.12)',
                })}
              >
                <BottomNavigation showLabels>
                  <BottomNavigationAction
                    component={NavLink}
                    to={ROOT}
                    label={t('welcome')}
                    icon={<HomeIcon />}
                    sx={{
                      flex: 1,
                      maxWidth: 'none',
                      '&.active .MuiSvgIcon-root, &.active .MuiBottomNavigationAction-label': {
                        color: 'primary.main',
                      },
                    }}
                  />
                  {actor?.actorEmail && (
                    <BottomNavigationAction
                      component={NavLink}
                      to={LIBRARY}
                      label={t('library')}
                      icon={<FavoriteIcon />}
                      sx={{
                        flex: 1,
                        maxWidth: 'none',
                        '&.active .MuiSvgIcon-root, &.active .MuiBottomNavigationAction-label': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  )}
                </BottomNavigation>
              </Box>
            )}
          </NoSnippet>
        </>
      ) : (
        <>
          {/* Default layout on desktop: add bottom padding to avoid player overlap */}
          <Box sx={({ spacing }) => ({ pb: `calc(${spacing(2)} + 180px)` })}>
            {children ?? (
              <Outlet context={{
                desktopPlayerPlaceholderRef: desktopPlaceholderRef,
                mobilePlayerPlaceholderRef: mobilePlaceholderRef,
              }} />
            )}
          </Box>
          <NoSnippet>
            {isOnWelcomePage && <FooterLinks />}
          </NoSnippet>
        </>
      )}
      <NoSnippet>
      {/* Persistent mini-player placeholder on non-video pages (only when a video is active) */}
      {!isVideoPage && currentVideo && (
        // Mini-player placeholder for measuring and layout (offset upward for bottom chrome)
        <Box
          ref={miniPlaceholderRef}
          sx={({ spacing, palette, zIndex }) => ({
            position: 'fixed',
            // Raise by chrome strip height (spacing(6)) to avoid bottom chrome overlap
            bottom: isMobile
              ? `calc(${spacing(9)} + ${spacing(6)})`
              : `calc(${spacing(2)} + ${spacing(6)})`,
            // Offset on desktop to avoid left drawer overlap (collapsed or expanded)
            left: isMobile
              ? spacing(2)
              : `calc(${drawerOpen ? fullDrawerWidth : miniDrawerWidth} + ${spacing(2)})`,
            width: isMobile ? 240 : 320,
            height: (isMobile ? 240 : 320) * 9 / 16,
            bgcolor: palette.background.paper,
            visibility: 'hidden',
            boxShadow: 3,
            borderRadius: 1,
            overflow: 'hidden',
            zIndex: zIndex.drawer + 1,
          })}
        />
      )}
      {/* Persistent full-size player portal */}
      <PersistentYtPlayer
        desktopPlaceholderRef={desktopPlaceholderRef}
        mobilePlaceholderRef={mobilePlaceholderRef}
        miniPlaceholderRef={miniPlaceholderRef}
        isVideoPage={isVideoPage}
      />
      </NoSnippet>
      </Box>
    </Box>
  );
};

export default Layout;